// The only place that reads raw API discount / product payloads.
// Strings are parsed to numbers once, field casing is unified, and customer
// names / phones are dropped here so they never reach app state or logs.
import { toMinor } from "./money"
import {
	RawAutomaticDiscount,
	RawBuyXGetYDiscount,
	RawCoupon,
	RawCustomerEligibility,
	RawGetProduct,
	RawPayXGetFreeDiscount,
	RawProductAttributes,
	RawProductDiscount,
	RawRestrictions,
} from "./rawTypes"
import {
	AutomaticDiscount,
	BuyRequirement,
	BuyXGetYDiscount,
	CartLineInput,
	Coupon,
	Discount,
	Eligibility,
	GetOption,
	PayXGetFreeDiscount,
	Restriction,
	TaxRate,
	ValueType,
} from "./types"

// Coupon restriction ids used by the coupon API (see validateManualDiscount)
const COUPON_RESTRICTION_MIN_AMOUNT = 32
const COUPON_RESTRICTION_MIN_QUANTITY = 33

const toNumberOrNull = (value: unknown): number | null => {
	if (value === null || value === undefined || value === "") return null
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : null
}

const toTime = (value: string | null | undefined): number | null => {
	if (!value) return null
	const time = Date.parse(value)
	return Number.isNaN(time) ? null : time
}

const toValueType = (code: string | null | undefined): ValueType =>
	String(code ?? "").toUpperCase() === "FIXED" ? "FIXED" : "PERCENTAGE"

const productName = (item: { productName: string, variantName: string | null }) =>
	item.variantName && item.variantName !== item.productName ? `${item.productName} - ${item.variantName}` : item.productName

export const normaliseEligibility = (raw: RawCustomerEligibility | null | undefined): Eligibility => {
	switch (raw?.type) {
		case "FOR_ALL":
			return { type: "FOR_ALL" }
		case "CUSTOMERS":
			return { type: "CUSTOMERS", customerIds: [...(raw.customerIds ?? [])] }
		case "CUSTOMER_GROUP":
			// The top-level customerIds is empty for groups; only the per-group lists count
			return {
				type: "CUSTOMER_GROUP",
				groups: (raw.customerGroups ?? []).map((group) => ({ groupId: group.id, customerIds: [...(group.customerIds ?? [])] })),
			}
		default:
			return { type: "UNSUPPORTED", rawType: String(raw?.type ?? "UNKNOWN") }
	}
}

export const normaliseRestriction = (basedOn: string | number | null | undefined, minValue: unknown): Restriction => {
	const min = toNumberOrNull(minValue)
	if (min == null || min <= 0) return { kind: "NONE" }
	const key = String(basedOn ?? "").toUpperCase()
	if (basedOn === COUPON_RESTRICTION_MIN_QUANTITY || key.includes("QUANTITY")) return { kind: "MIN_QUANTITY", min }
	if (basedOn === COUPON_RESTRICTION_MIN_AMOUNT || key.includes("AMOUNT") || key.includes("PURCHASE")) return { kind: "MIN_AMOUNT", min: toMinor(min) }
	return { kind: "NONE" }
}

const datesOf = (restrictions: RawRestrictions | null | undefined) => ({
	startsAt: toTime(restrictions?.startDate),
	endsAt: toTime(restrictions?.endDate),
})

export const normaliseAutomatic = (raw: RawAutomaticDiscount): AutomaticDiscount => {
	const valueType = toValueType(raw.discounttypecode)
	const value = toNumberOrNull(raw.discountvalue) ?? 0
	const cap = toNumberOrNull(raw.maxamount)
	const appliesTo = String(raw.appliesTo ?? "").toUpperCase()
	return {
		kind: "AUTOMATIC",
		id: raw.id,
		title: raw.title,
		valueType,
		value: valueType === "FIXED" ? toMinor(value) : value,
		cap: cap != null && cap > 0 ? toMinor(cap) : null,
		scope:
			appliesTo === "BRAND" && raw.brandid != null
				? { type: "BRAND", brandId: raw.brandid, brandName: raw.brandName ?? null }
				: appliesTo === "CATEGORY" && raw.categoryid != null
					? { type: "CATEGORY", categoryId: raw.categoryid, categoryName: raw.categoryName ?? null }
					// Filled from the store index: the variants whose request returned this id
					: { type: "PRODUCT", variantIds: [] },
		eligibility: normaliseEligibility(raw.customerEligibility),
		restriction: normaliseRestriction(raw.restrictions?.restrictionBasedOn, raw.restrictions?.minValue ?? raw.minvalue),
		...datesOf(raw.restrictions),
	}
}

const normaliseGetOption = (raw: RawGetProduct): GetOption => ({
	productId: raw.productId,
	variantId: raw.id,
	name: productName(raw),
	price: toMinor(raw.price),
	// Single-product get items carry no stock; the add-to-cart flow checks it
	inStock: raw.stock === undefined || raw.stock === null || raw.isUnlimitedStock === true || raw.stock > 0 || raw.stock === -1,
	labelDiscount: raw.discountAmount != null ? toMinor(raw.discountAmount) : null,
	labelFinalPrice: raw.finalPrice != null ? toMinor(raw.finalPrice) : null,
})

export const normaliseBuyXGetY = (raw: RawBuyXGetYDiscount): BuyXGetYDiscount => {
	const buy: BuyRequirement[] = (raw.buydata ?? []).map((item) =>
		"categoryId" in item
			? { type: "CATEGORY", categoryId: item.categoryId, name: item.categoryName, quantity: item.quantity || 1 }
			: { type: "PRODUCT", productId: item.productId, variantId: item.id ?? null, name: productName(item), quantity: item.quantity || 1 }
	)
	const getdata = raw.getdata
	const isCategory = "products" in getdata
	return {
		kind: "BUY_X_GET_Y",
		id: raw.id,
		title: raw.title,
		buy,
		get: isCategory
			? {
				type: "CATEGORY",
				categoryId: getdata.categoryId,
				name: getdata.categoryName ?? getdata.name ?? "",
				quantity: getdata.quantity || 1,
				percent: toNumberOrNull(getdata.getyPercentage) ?? 0,
				options: (getdata.products ?? []).map(normaliseGetOption),
			}
			: {
				type: "PRODUCT",
				categoryId: null,
				name: productName(getdata),
				quantity: getdata.quantity || 1,
				percent: toNumberOrNull(getdata.getyPercentage) ?? 0,
				options: [normaliseGetOption(getdata)],
			},
		eligibility: normaliseEligibility(raw.customerEligibility),
		restriction: normaliseRestriction(raw.restrictions?.restrictionBasedOn, raw.restrictions?.minValue),
		...datesOf(raw.restrictions),
	}
}

export const normalisePayXGetFree = (raw: RawPayXGetFreeDiscount): PayXGetFreeDiscount => ({
	kind: "PAY_X_GET_FREE",
	id: raw.id,
	title: raw.title,
	threshold: toMinor(raw.minValue ?? raw.restrictions?.minValue),
	freeProducts: (raw.freeProducts ?? []).map((item) => ({
		productId: item.productId,
		variantId: item.id,
		name: productName(item),
		price: toMinor(item.price),
		inStock: item.isUnlimitedStock === true || item.stock === -1 || (item.stock ?? 0) > 0,
	})),
	eligibility: normaliseEligibility(raw.customerEligibility),
	// The threshold is the only restriction; it is checked separately
	restriction: { kind: "NONE" },
	...datesOf(raw.restrictions),
})

export const normaliseProductDiscount = (raw: RawProductDiscount | null | undefined): Discount[] => [
	...(raw?.automaticDiscount ?? []).map(normaliseAutomatic),
	...(raw?.buyXgetYDiscount ?? []).map(normaliseBuyXGetY),
	...(raw?.payXGetFreeDiscount ?? []).map(normalisePayXGetFree),
]

// ---- Discount store: one entry per discount id, plus which variants returned it ----

export type DiscountStore = {
	byId: Record<number, Discount>
	byVariant: Record<number, number[]>
}

export const emptyDiscountStore = (): DiscountStore => ({ byId: {}, byVariant: {} })

// Replaces what the store knows about one variant, deduplicating by discount id.
// Discounts no variant references any more (deactivated / expired server side) are dropped.
export const mergeVariantDiscounts = (store: DiscountStore, variantId: number, discounts: Discount[]): DiscountStore => {
	const byVariant = { ...store.byVariant, [variantId]: discounts.map((discount) => discount.id) }
	const byId: Record<number, Discount> = { ...store.byId }
	discounts.forEach((discount) => { byId[discount.id] = discount })
	const referenced = new Set(Object.values(byVariant).flat())
	Object.keys(byId).forEach((id) => { if (!referenced.has(Number(id))) delete byId[Number(id)] })
	return { byId, byVariant }
}

// Forgets every variant except `keepVariantIds` (and the discounts only they referenced),
// so the next lookup of a forgotten variant goes back to the API.
export const retainVariants = (store: DiscountStore, keepVariantIds: number[]): DiscountStore => {
	const keep = new Set(keepVariantIds)
	const byVariant: Record<number, number[]> = {}
	Object.entries(store.byVariant).forEach(([variantId, ids]) => { if (keep.has(Number(variantId))) byVariant[Number(variantId)] = ids })
	const referenced = new Set(Object.values(byVariant).flat())
	const byId: Record<number, Discount> = {}
	Object.entries(store.byId).forEach(([id, discount]) => { if (referenced.has(Number(id))) byId[Number(id)] = discount })
	return { byId, byVariant }
}

export const hasVariantDiscounts =(store: DiscountStore | null | undefined, variantId: number) =>
	Boolean(store?.byVariant?.[variantId])

// Engine input: each discount once, PRODUCT scopes resolved to the variants that returned them
export const discountsFromStore = (store: DiscountStore | null | undefined): Discount[] => {
	if (!store) return []
	const variantsById: Record<number, number[]> = {}
	Object.entries(store.byVariant).forEach(([variantId, ids]) => {
		ids.forEach((id) => { (variantsById[id] ??= []).push(Number(variantId)) })
	})
	return Object.values(store.byId).map((discount) =>
		discount.kind === "AUTOMATIC" && discount.scope.type === "PRODUCT"
			? { ...discount, scope: { type: "PRODUCT", variantIds: variantsById[discount.id] ?? [] } }
			: discount
	)
}

export const discountsForVariant = (store: DiscountStore | null | undefined, variantId: number): Discount[] =>
	(store?.byVariant?.[variantId] ?? []).map((id) => store!.byId[id]).filter(Boolean)

// ---- Product attributes / cart lines / coupon ----

export type ProductAttributes = {
	brandId: number | null
	categoryIds: number[]
	taxes: TaxRate[]
}

// taxPercent is the rate the server resolved for this store (zone rate or the "other zone" rate)
export const normaliseProductAttributes = (raw: RawProductAttributes | null | undefined): ProductAttributes => ({
	brandId: raw?.brandId ?? null,
	categoryIds: (raw?.categories ?? []).map((category) => category.categoryId),
	taxes: raw?.isTaxable === false
		? []
		: (raw?.productTaxes ?? [])
			.map((tax) => ({ id: tax.id, name: tax.name, rate: toNumberOrNull(tax.taxPercent) ?? 0, inclusive: tax.isIncludeTaxInProduct === true }))
			.filter((tax) => tax.rate > 0),
})

export const normaliseCartLine = (line: {
	variantId: number, productId: number, name: string, variantName?: string | null, price: number, quantity: number,
	brandId?: number | null, categoryIds?: number[] | null, taxes?: TaxRate[] | null
}): CartLineInput => ({
	key: String(line.variantId),
	productId: line.productId,
	variantId: line.variantId,
	name: line.variantName ? `${line.name} - ${line.variantName}` : line.name,
	unitPrice: toMinor(line.price),
	quantity: Number(line.quantity) || 0,
	brandId: line.brandId ?? null,
	categoryIds: line.categoryIds ?? [],
	taxes: line.taxes ?? [],
})

export const normaliseCoupon = (raw: RawCoupon | null | undefined): Coupon | null => {
	if (!raw) return null
	const valueType = toValueType(raw.discountType?.discountTypeCode)
	const value = toNumberOrNull(raw.discountValue) ?? 0
	const cap = toNumberOrNull(raw.maxAmount)
	return {
		id: raw.id,
		code: raw.promoCode,
		title: raw.promoCode,
		valueType,
		value: valueType === "FIXED" ? toMinor(value) : value,
		cap: cap != null && cap > 0 ? toMinor(cap) : null,
		restriction: normaliseRestriction(raw.restrictionBasedOn, raw.minValue),
		startsAt: toTime(raw.startDate),
		endsAt: toTime(raw.endDate),
	}
}
